import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { Building2, ExternalLink } from "lucide-react";
import { getImageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const PLACEHOLDER = [
  { name: "Kementerian Sosial RI", category: "Pemerintah Pusat" },
  { name: "Kemenko PMK", category: "Pemerintah Pusat" },
  { name: "Pemprov Jawa Barat", category: "Pemerintah Daerah" },
  { name: "Pemkab Bandung Barat", category: "Pemerintah Daerah" },
  { name: "PT Pertamina", category: "BUMN" },
  { name: "PT Telkom Indonesia", category: "BUMN" },
  { name: "PT BNI (Persero) Tbk", category: "BUMN" },
  { name: "PT Bank Mandiri", category: "BUMN" },
  { name: "Yayasan Bakrie Amanah", category: "Filantropi" },
  { name: "Dompet Dhuafa", category: "Filantropi" },
  { name: "Universitas Padjadjaran", category: "Akademisi" },
  { name: "Institut Teknologi Bandung", category: "Akademisi" },
  { name: "Universitas Indonesia", category: "Akademisi" },
  { name: "UNICEF Indonesia", category: "Internasional" },
  { name: "World Vision", category: "Internasional" },
  { name: "UNDP", category: "Internasional" },
];

export default function Partners() {
  const [items, setItems] = useState([]);
  const { settings } = useSettings();
  const pageData = settings?.partners_page || {};

  useEffect(() => {
    api.get("/partners").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const partners = items.length > 0 ? items : PLACEHOLDER;
  const grouped = partners.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  return (
    <div data-testid="page-partners">
      <SeoHead title={{ id: "Mitra", en: "Partners" }} description={{ id: pageData.description || "Mitra strategis lintas sektor Sauyunan Foundation.", en: "Cross-sector strategic partners of Sauyunan Foundation." }} />
      <PageHeader
        eyebrow={pageData.eyebrow || "Mitra Strategis"}
        title={pageData.title || "Kolaborasi lintas sektor untuk dampak berkelanjutan"}
        description={pageData.description || "Dampak yang besar hanya dapat dicapai melalui kolaborasi yang kuat. Kami membuka kemitraan dengan berbagai pihak."}
        image={pageData.image ? getImageUrl(pageData.image) : undefined}
      />

      <section className="section-y bg-white">
        <div className="container-sf space-y-14">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="flex items-baseline gap-4 mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{cat}</h2>
                <span className="text-sm text-slate-500">{list.length} mitra</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {list.map((p, i) => (
                  <div key={i} className="card-sf p-6 flex items-center gap-4" data-testid={`partner-${cat.replace(/\s/g,'-').toLowerCase()}-${i}`}>
                    {p.logo_url ? (
                      <img src={getImageUrl(p.logo_url)} alt={p.name} className="h-14 w-14 object-contain shrink-0" />
                    ) : (
                      <div className="h-14 w-14 rounded-xl bg-[var(--brand-soft-blue)] flex items-center justify-center shrink-0">
                        <Building2 className="h-6 w-6 text-[var(--brand-blue)]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 leading-tight">{p.name}</div>
                      {p.website && (
                        <a href={p.website} target="_blank" rel="noreferrer" className="text-xs text-[var(--brand-blue)] hover:text-[var(--brand-orange)] flex items-center gap-1 mt-1">
                          <ExternalLink className="h-3 w-3" /> Kunjungi
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="container-sf">
          <div className="rounded-3xl bg-[var(--brand-blue)] text-white p-10 md:p-14 text-center relative overflow-hidden">
            <div className="grain-overlay" />
            <div className="relative">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">Menjadi mitra Sauyunan Foundation?</h3>
              <p className="text-slate-200 max-w-xl mx-auto mb-6">Kami mengundang pemerintah, dunia usaha, akademisi, dan komunitas untuk membangun ekosistem dampak bersama.</p>
              <a href="/kontak" className="btn-primary">Hubungi Tim Kemitraan</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
